import * as THREE from 'three';
import type { InteractableDef } from '../../zones/types';
import type { AssetLibrary } from '../assets';
import type { LightSource } from './PropLights';

/** Glow colour per interactable kind. */
const KIND_COLOR: Partial<Record<InteractableDef['kind'], number>> = {
  gather: 0x9be07a,
  seed: 0x9dff6a,
  chest: 0xffc860,
  waystone: 0x56e0d2,
  vista: 0xfff0b0,
  lore: 0xc8a8ff,
  fish: 0x7ac8ff,
  dig: 0xe0b070,
  heart: 0xff6a9a,
  station: 0xffb050,
  questItem: 0xffe070,
};

/** World model shown for an interactable (null: glow only). */
function modelFor(it: InteractableDef): string | null {
  switch (it.kind) {
    case 'waystone':
      return 'waystone';
    case 'gather':
      return it.data?.node === 'ore' ? 'node_ore' : it.data?.node === 'timber' ? 'node_timber' : 'node_herb';
    case 'dig':
      return 'node_dig';
    case 'seed':
      return 'glimmerseed';
    case 'chest':
      return 'chest_closed';
    default:
      return null;
  }
}

let sparkTex: THREE.Texture | null = null;
function sparkTexture(): THREE.Texture {
  if (sparkTex) return sparkTex;
  const s = 64;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.2, 'rgba(255,255,255,0.7)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, s, s);
  // Four-point star.
  g.fillStyle = 'rgba(255,255,255,0.9)';
  g.beginPath();
  g.moveTo(s / 2, 2);
  g.lineTo(s / 2 + 3, s / 2 - 3);
  g.lineTo(s - 2, s / 2);
  g.lineTo(s / 2 + 3, s / 2 + 3);
  g.lineTo(s / 2, s - 2);
  g.lineTo(s / 2 - 3, s / 2 + 3);
  g.lineTo(2, s / 2);
  g.lineTo(s / 2 - 3, s / 2 - 3);
  g.closePath();
  g.fill();
  sparkTex = new THREE.CanvasTexture(c);
  sparkTex.colorSpace = THREE.SRGBColorSpace;
  return sparkTex;
}

function glowSprite(color: THREE.Color, size: number, intensity: number): THREE.Sprite {
  const m = new THREE.SpriteMaterial({ map: sparkTexture(), color: color.clone().multiplyScalar(intensity), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true });
  const s = new THREE.Sprite(m);
  s.scale.setScalar(size);
  s.renderOrder = 13;
  return s;
}

function ringMesh(color: THREE.Color, inner: number, outer: number, opacity: number): THREE.Mesh {
  const m = new THREE.MeshBasicMaterial({ color: color.clone().multiplyScalar(1.6), transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
  const r = new THREE.Mesh(new THREE.RingGeometry(inner, outer, 48), m);
  r.rotation.x = -Math.PI / 2;
  r.position.y = 0.06;
  r.renderOrder = 7;
  return r;
}

function glowMat(color: THREE.Color, intensity: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: color.clone().multiplyScalar(0.3), emissive: color, emissiveIntensity: intensity, roughness: 0.35 });
}

export interface Marker {
  group: THREE.Group;
  /** Floats and spins above the marker. */
  bob: THREE.Object3D | null;
  bobBase: number;
  /** Expanding ripple rings (fishing). */
  ripples: THREE.Mesh[];
  spin: THREE.Object3D[];
  light: LightSource | null;
  phase: number;
}

/**
 * World marker for an interactable: the real model where one exists (life
 * skill nodes, waystones, chests, glimmerseeds) plus a floating glint, a
 * ground ring and, for waystones and quest items, a light. Models load
 * asynchronously; the glow shows at once.
 */
export function buildMarker(it: InteractableDef, lib: AssetLibrary, groundY: number, disposed: () => boolean): Marker {
  const g = new THREE.Group();
  g.name = `marker_${it.id}`;
  const color = new THREE.Color(KIND_COLOR[it.kind] ?? 0xffffff);
  const mk: Marker = { group: g, bob: null, bobBase: 0.9, ripples: [], spin: [], light: null, phase: Math.random() * 10 };
  g.position.set(it.x, groundY, it.z);
  const modelId = modelFor(it);
  if (modelId) {
    void lib.load(modelId).then((t) => {
      if (disposed() || t.placeholder) return;
      const m = lib.instantiate(t);
      m.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          mesh.userData.shared = true;
        }
      });
      if (it.kind === 'seed') m.scale.setScalar(1.6);
      g.add(m);
    });
  }
  switch (it.kind) {
    case 'waystone': {
      const crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), glowMat(color, 1.8));
      crystal.scale.set(0.7, 1.6, 0.7);
      mk.bob = crystal;
      mk.bobBase = 4.4;
      g.add(crystal, ringMesh(color, 3.05, 3.2, 0.45));
      const halo = glowSprite(color, 1.5, 0.8);
      crystal.add(halo);
      mk.light = { kind: 'light', pos: new THREE.Vector3(it.x, groundY + 2.2, it.z), color: color.clone(), intensity: 3, distance: 8, ground: groundY };
      break;
    }
    case 'fish': {
      for (let i = 0; i < 2; i++) {
        const r = ringMesh(color, 0.9, 1.0, 0.5);
        mk.ripples.push(r);
        g.add(r);
      }
      const float = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), new THREE.MeshStandardMaterial({ color: 0xd83a2a, roughness: 0.4 }));
      mk.bob = float;
      mk.bobBase = 0.08;
      g.add(float, glowSprite(color, 1.0, 1.2));
      break;
    }
    case 'vista': {
      const beam = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18, 0.35, 7, 12, 1, true).translate(0, 3.5, 0),
        new THREE.MeshBasicMaterial({ color: color.clone().multiplyScalar(0.9), transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false }),
      );
      const eye = glowSprite(color, 1.1, 2.2);
      mk.bob = eye;
      mk.bobBase = 2.2;
      g.add(beam, eye, ringMesh(color, 1.0, 1.15, 0.6));
      break;
    }
    case 'questItem': {
      const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.28, 0), glowMat(color, 2.8));
      mk.bob = gem;
      mk.bobBase = 1.1;
      gem.add(glowSprite(color, 1.4, 1.6));
      g.add(gem, ringMesh(color, 0.7, 0.85, 0.7));
      mk.light = { kind: 'light', pos: new THREE.Vector3(it.x, groundY + 1.1, it.z), color: color.clone(), intensity: 2.5, distance: 5, ground: groundY };
      break;
    }
    case 'lore':
    case 'heart':
    case 'station': {
      const geo = it.kind === 'lore' ? new THREE.BoxGeometry(0.42, 0.56, 0.08) : it.kind === 'heart' ? new THREE.IcosahedronGeometry(0.26, 1) : new THREE.TorusGeometry(0.28, 0.07, 8, 20);
      const icon = new THREE.Mesh(geo, glowMat(color, 2.4));
      mk.bob = icon;
      mk.bobBase = 1.3;
      icon.add(glowSprite(color, 1.3, 1.4));
      g.add(icon, ringMesh(color, 0.75, 0.9, 0.55));
      break;
    }
    default: {
      // Nodes, dig sites, seeds and chests: a floating glint above the model.
      const high = it.kind === 'gather' && it.data?.node === 'timber' ? 1.7 : it.kind === 'chest' ? 1.35 : it.kind === 'seed' ? 1.0 : 1.3;
      const glint = glowSprite(color, it.kind === 'seed' ? 0.9 : 0.75, it.kind === 'seed' ? 2.4 : 1.8);
      mk.bob = glint;
      mk.bobBase = high;
      g.add(glint, ringMesh(color, it.kind === 'seed' ? 0.4 : 0.85, it.kind === 'seed' ? 0.48 : 0.97, 0.35));
      if (it.kind === 'seed') mk.light = { kind: 'light', pos: new THREE.Vector3(it.x, groundY + 0.7, it.z), color: color.clone(), intensity: 1.6, distance: 3.5, ground: groundY };
      break;
    }
  }
  if (mk.bob) mk.bob.position.y = mk.bobBase;
  g.userData.bob = mk.bob;
  g.userData.kind = it.kind;
  return mk;
}

export function updateMarker(mk: Marker, time: number, dt: number): void {
  const t = time + mk.phase;
  if (mk.bob) {
    const amp = mk.ripples.length ? 0.04 : 0.12;
    mk.bob.position.y = mk.bobBase + Math.sin(t * 2) * amp;
    mk.bob.rotation.y += dt;
  }
  mk.ripples.forEach((r, i) => {
    const k = ((t * 0.5 + i * 0.5) % 1 + 1) % 1;
    r.scale.setScalar(0.4 + k * 1.3);
    (r.material as THREE.MeshBasicMaterial).opacity = 0.55 * (1 - k);
  });
}

export function disposeMarker(mk: Marker): void {
  mk.group.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.userData.shared) return;
    if (m.isMesh) {
      m.geometry.dispose();
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) mat.dispose();
    } else if ((o as THREE.Sprite).isSprite) {
      (o as THREE.Sprite).material.dispose();
    }
  });
}
